import React, { useState } from "react";

import styled from "styled-components";

import BackgroundImageComponent from "app/components/bgImage/BackgroundImageComponent";
import ColorStack from "app/components/colorsStack/ColorStack";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { BulletLegends } from "app/components/React/Legends/BulletLegends";
import { MiniFlexTable } from "app/components/React/Table/FlexTable/Mini/MiniFlexTable";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import { TitleContainer } from "app/components/React/Widgets/WidgetsTop";
import { WidgetFooter } from "app/components/widget/WidgetFooter";
import { StyledSubtitle } from "app/components/widget-title/StyledComponents";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { NoData, TableLoader } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import durationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { isAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { generateLastMonthSubtitleFilters } from "./utlis";

const TopKeywordsContainer = styled.div`
    padding: 24px 24px 0 24px;
`;

const LegendsWrapper = styled.div`
    margin: 16px 0;
`;

const TopKeywordsTable = styled.div`
    padding-top: 8px;
`;

const EndPoint = "widgetApi/SearchKeywordsV2/WebsitePerformance/Table";
const MonthlyTimeGranularity = "Monthly";
const TableRowsAmount = 5;
const buttonText = "WWO.SearchTraffic.Organic.topKeywords.cta.button";

const TableCompareColumns = [
    {
        field: "SearchTerm",
        get displayName() {
            return translate("wwo.searchTraffic.organic.topKeywords.search.term");
        },
        cellTemplate: "/app/components/table/templates/cell-keyword-dashboard.html",
        headerCellTemplate: "/app/components/table/templates/default-cell-header.html",
        disableHeaderCellHover: false,
        sortable: false,
        sortDirection: "desc",
        isSorted: false,
        width: 220,
        groupable: false,
        tooltip: false,
        progressBarTooltip: "",
        showTotalCount: false,
        format: "None",
        visible: true,
        toggleable: true,
    },
    {
        field: "TotalShare",
        get displayName() {
            return translate("wwo.searchTraffic.organic.topKeywords.traffic.share");
        },
        cellTemplate: "/app/components/table/templates/default-cell.html",
        headerCellTemplate: "/app/components/table/templates/default-cell-header.html",
        disableHeaderCellHover: false,
        sortable: false,
        sortDirection: "desc",
        isSorted: false,
        width: "115px",
        groupable: false,
        tooltip: false,
        progressBarTooltip: "",
        showTotalCount: false,
        format: "smallNumbersPercentage:2",
        visible: true,
        toggleable: true,
    },
    {
        field: "SiteOrigins",
        get displayName() {
            return translate("wwo.searchTraffic.organic.topKeywords.compare.group.share");
        },
        cellTemplate: "/app/components/table/templates/group-traffic-share-dashboard.html",
        headerCellTemplate: "/app/components/table/templates/default-cell-header.html",
        disableHeaderCellHover: false,
        sortable: false,
        sortDirection: "desc",
        isSorted: false,
        minWidth: 250,
        groupable: false,
        tooltip: false,
        progressBarTooltip: "",
        showTotalCount: false,
        format: "percentagesign",
        visible: true,
        toggleable: true,
    },
    {
        field: "KwVolume",
        get displayName() {
            return translate("wwo.searchTraffic.organic.topKeywords.compare.volume");
        },
        cellTemplate: "/app/components/table/templates/default-cell.html",
        headerCellTemplate: "/app/components/table/templates/default-cell-header.html",
        disableHeaderCellHover: false,
        sortable: false,
        sortDirection: "desc",
        isSorted: false,
        width: 85,
        groupable: false,
        tooltip: false,
        progressBarTooltip: "",
        showTotalCount: false,
        format: "swPosition",
        visible: true,
        toggleable: true,
    },
    {
        field: "CPC",
        get displayName() {
            return translate("wwo.searchTraffic.organic.topKeywords.compare.cpc");
        },
        cellTemplate: "/app/components/table/templates/default-cell.html",
        headerCellTemplate: "/app/components/table/templates/default-cell-header.html",
        disableHeaderCellHover: false,
        sortable: false,
        sortDirection: "desc",
        isSorted: false,
        width: 85,
        groupable: false,
        tooltip: false,
        progressBarTooltip: "",
        showTotalCount: false,
        format: "CPC",
        visible: true,
        toggleable: true,
    },
];

const TableColumns = [
    {
        field: "SearchTerm",
        get displayName() {
            return translate("wwo.searchTraffic.organic.topKeywords.search.term");
        },
        cellTemplate: "/app/components/table/templates/wwo-keyword-cell.html",
        headerCellTemplate: "/app/components/table/templates/default-cell-header.html",
        disableHeaderCellHover: false,
        sortable: true,
        sortDirection: "desc",
        isSorted: false,
        minWidth: 130,
        maxWidth: 220,
        groupable: false,
        tooltip: false,
        progressBarTooltip: "",
        showTotalCount: false,
        format: "None",
        visible: true,
        toggleable: true,
    },
    {
        field: "TotalShare",
        get displayName() {
            return translate("wwo.searchTraffic.organic.topKeywords.traffic.share");
        },
        cellTemplate: "/app/components/table/templates/traffic-share.html",
        headerCellTemplate: "/app/components/table/templates/default-cell-header.html",
        disableHeaderCellHover: false,
        sortable: false,
        sortDirection: "desc",
        isSorted: false,
        minWidth: 130,
        groupable: false,
        tooltip: false,
        progressBarTooltip: "",
        showTotalCount: false,
        format: "percentagesign",
        visible: true,
        toggleable: true,
    },
    {
        field: "Change",
        get displayName() {
            return translate("wwo.searchTraffic.organic.topKeywords.change");
        },
        cellTemplate: "/app/components/table/templates/change-percentage.html",
        headerCellTemplate: "/app/components/table/templates/default-cell-header.html",
        headerComponent: DefaultCellHeader,
        disableHeaderCellHover: false,
        sortable: false,
        sortDirection: "desc",
        isSorted: false,
        width: 85,
        groupable: false,
        tooltip: "widget.table.tooltip.newsearchkeywordswebsiteoverview.change",
        progressBarTooltip: "",
        showTotalCount: false,
        format: "percentagesign",
        visible: true,
        toggleable: true,
    },
];

const CompareOptions = {
    downloadUrl: false,
    showLoadMore: false,
    filterField: false,
    tableType: "swTable--simple",
    loadingSize: 5,
    dataErrorMessageTop: "appstorekeywords.topkeywords.nodata",
    dataErrorMessageBottom: "",
    hideHeader: false,
    colorSource: "main",
    hideBorders: false,
    hideRowsBorders: true,
    trackName: "Marketing Mix/Top Search Terms",
    stickyHeader: false,
    scrollableTable: false,
    sortedColumnAddedWidth: false,
    onSort: false,
    enableRowSelection: false,
    numberOfFixedColumns: 1,
    tableSelectionTrackingParam: "",
    metric: "NewSearchKeywordsWebsiteOverview",
};

const Options = {
    showTitle: true,
    showTitleTooltip: false,
    titleType: "text",
    showSubtitle: true,
    desktopOnly: true,
    showLegend: false,
    titlePaddingBottom: "19px",
    showSettings: false,
    showTopLine: false,
    showFrame: true,
    hideHeader: true,
    hideBorders: true,
    ctaButton: "wwo.search.terms.button",
    tableType: "swTable--simple",
};

export const OrganicTopKeywords = (props) => {
    const { noDataState, setNoDataState, queryParams, routingParams, getCtaLink, unlockHook } = props;
    const OrganicQueryParams = {
        ...props.queryParams,
        SourceType: SourceType.ORGANIC,
        pageSize: TableRowsAmount,
    };
    const IncludeOrganic = true;
    const IncludePaid = false;
    const Title = "WWO.SearchTraffic.Organic.topKeywords.Title";
    const ToolTipKey = "WWO.SearchTraffic.Organic.topKeywords.Title.Tooltip";
    const DataState = "topKeywordsOrganic";
    const trackingLabel = "wwo search traffic/top keywords/organic";
    const Constants = {
        IncludeOrganic,
        IncludePaid,
        Title,
        ToolTipKey,
        DataState,
        queryParams,
        trackingLabel,
    };
    return (
        <TopKeywords
            {...Constants}
            noDataState={noDataState}
            setNoDataState={setNoDataState}
            routingParams={routingParams}
            queryParams={OrganicQueryParams}
            getCtaLink={getCtaLink}
            unlockHook={unlockHook}
        />
    );
};

export const PaidTopKeywords = (props) => {
    const { noDataState, setNoDataState, queryParams, routingParams, getCtaLink, unlockHook } = props;
    const PaidQueryParams = {
        ...queryParams,
        SourceType: SourceType.PAID,
        pageSize: TableRowsAmount,
    };
    const IncludeOrganic = false;
    const IncludePaid = true;
    const Title = "WWO.SearchTraffic.Paid.topKeywords.Title";
    const ToolTipKey = "WWO.SearchTraffic.Paid.topKeywords.Title.Tooltip";
    const DataState = "topKeywordsPaid";
    const trackingLabel = "wwo search traffic/top keywords/paid";
    const Constants = {
        IncludeOrganic,
        IncludePaid,
        Title,
        ToolTipKey,
        DataState,
        queryParams,
        trackingLabel,
    };
    return (
        <TopKeywords
            {...Constants}
            noDataState={noDataState}
            setNoDataState={setNoDataState}
            routingParams={routingParams}
            queryParams={PaidQueryParams}
            getCtaLink={getCtaLink}
            unlockHook={unlockHook}
        />
    );
};

export enum SourceType {
    ALL = "All",
    ORGANIC = "Organic",
    PAID = "Paid",
}

export const OrganicTopKeywordsCompare = (props) => {
    const {
        noDataState,
        setNoDataState,
        queryParams,
        routingParams,
        getCtaLink,
        unlockHook,
        hideFooter = false,
    } = props;
    const isCompare = true;
    const chartMainColors = new ColorStack(CHART_COLORS.chartMainColors);
    const OrganicQueryParams = {
        ...props.queryParams,
        SourceType: SourceType.ORGANIC,
        pageSize: TableRowsAmount,
    };
    const IncludeOrganic = true;
    const IncludePaid = false;
    const Title = "wwo.searchTraffic.organic.topKeywords.compare.title";
    const ToolTipKey = "wwo.searchTraffic.organic.topKeywords.compare.title.tooltip";
    const DataState = "topKeywordsOrganic";
    const trackingLabel = "wwo search traffic/top keywords compare/ organic";
    chartMainColors.reset();
    const items = queryParams.keys.split(",").map((item) => {
        return {
            name: item,
            color: chartMainColors.acquire(),
        };
    });
    const Constants = {
        IncludeOrganic,
        IncludePaid,
        Title,
        ToolTipKey,
        DataState,
        queryParams,
        trackingLabel,
        items,
        isCompare,
    };
    return (
        <TopKeywords
            {...Constants}
            noDataState={noDataState}
            setNoDataState={setNoDataState}
            routingParams={routingParams}
            queryParams={OrganicQueryParams}
            getCtaLink={getCtaLink}
            unlockHook={unlockHook}
            hideFooter={hideFooter}
        />
    );
};

export const PaidTopKeywordsCompare = (props) => {
    const {
        noDataState,
        setNoDataState,
        queryParams,
        routingParams,
        getCtaLink,
        unlockHook,
        hideFooter = false,
    } = props;
    const chartMainColors = new ColorStack(CHART_COLORS.chartMainColors);
    const isCompare = true;
    const PaidQueryParams = {
        ...queryParams,
        SourceType: SourceType.PAID,
        pageSize: TableRowsAmount,
    };
    const IncludeOrganic = false;
    const IncludePaid = true;
    const Title = "wwo.searchTraffic.paid.topKeywords.compare.title";
    const ToolTipKey = "wwo.searchTraffic.paid.topKeywords.compare.title.tooltip";
    const trackingLabel = "wwo search traffic/top keywords compare/ paid";
    const DataState = "topKeywordsPaid";
    chartMainColors.reset();
    const items = queryParams.keys.split(",").map((item) => {
        return {
            name: item,
            color: chartMainColors.acquire(),
        };
    });
    const Constants = {
        IncludeOrganic,
        IncludePaid,
        Title,
        ToolTipKey,
        DataState,
        queryParams,
        trackingLabel,
        items,
        isCompare,
    };
    return (
        <TopKeywords
            {...Constants}
            noDataState={noDataState}
            setNoDataState={setNoDataState}
            routingParams={routingParams}
            queryParams={PaidQueryParams}
            getCtaLink={getCtaLink}
            unlockHook={unlockHook}
            hideFooter={hideFooter}
        />
    );
};

const TopKeywordsInner = (props) => {
    const {
        queryParams,
        noDataState,
        setNoDataState,
        routingParams,
        IncludeOrganic,
        IncludePaid,
        Title,
        ToolTipKey,
        DataState,
        trackingLabel,
        items,
        isCompare,
        getCtaLink,
        unlockHook,
        hideFooter = false,
    } = props;
    const [response, setResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const isAfterTrialUser = isAfterTrial();
    const { selectorHandlers } = useSelectorTrigger(trackingLabel);
    const setData = () => {
        if (isAfterTrialUser) {
            setIsLoading(false);
            return;
        }
        const fetchService = DefaultFetchService.getInstance();
        const { forAPI: lastMonthForAPI } = durationService.getDurationData(BasicDurations.LAST_MONTH);
        const getParams = {
            ...queryParams,
            from: lastMonthForAPI.from,
            to: lastMonthForAPI.to,
            isWindow: lastMonthForAPI.isWindow,
            timeGranularity: MonthlyTimeGranularity,
            duration: BasicDurations.LAST_MONTH,
        };
        const visitsDataPromise = fetchService.get(EndPoint, getParams);
        visitsDataPromise
            .then((Data) => parseResult(Data))
            .catch(() => setResponse(null))
            .finally(() => setIsLoading(false));
    };
    React.useEffect(setData, [queryParams]);

    const parseResult = (result) => {
        const data = { ...result, Records: result.Data };
        delete data.Data;

        if (isCompare) {
            data.Records = data.Records.map((item) => {
                return {
                    ...item,
                    url: proNavigator.href("organicsearch_keywordAnalysis_overview_2", {
                        ...routingParams,
                        keyword: item.SearchTerm,
                    }),
                };
            });
        }
        setResponse(data);
    };

    const isNoDataState = !response || Object.values(response).length === 0 || !response.Records.length;
    const href = getCtaLink({ ...routingParams, IncludeOrganic, IncludePaid, IncludeNoneBranded: true });

    React.useEffect(() => {
        if (isNoDataState !== noDataState[DataState]) {
            setNoDataState({
                ...noDataState,
                topKeywords: isNoDataState,
            });
        }
    }, [isLoading]);

    const subtitleFilters = generateLastMonthSubtitleFilters(
        queryParams.country,
        queryParams.webSource,
        selectorHandlers,
    );

    const renderAfterTrialComponent = () => {
        const singleCompareStr = isCompare ? "compare" : "single";
        const includePaidOraganicStr = IncludeOrganic ? "Organic" : "Paid";
        return (
            <div style={{ height: isCompare ? "200px" : "180px" }}>
                <BackgroundImageComponent
                    images={[
                        {
                            maxWidth: 2480,
                            imageUrl: `/images/website-performance/${singleCompareStr}/top${includePaidOraganicStr}KW_L.png`,
                        },
                    ]}
                />
            </div>
        );
    };

    return (
        <div>
            <TopKeywordsContainer>
                <TitleContainer>
                    <BoxTitle tooltip={i18nFilter()(ToolTipKey)}>{i18nFilter()(Title)}</BoxTitle>
                </TitleContainer>
                {isLoading ? (
                    <TableLoader />
                ) : isNoDataState && !isAfterTrialUser ? (
                    <NoData paddingTop={"66px"} />
                ) : (
                    <div>
                        <StyledSubtitle filters={subtitleFilters} />
                        {items ? (
                            <LegendsWrapper>
                                <BulletLegends legendItems={items} />
                            </LegendsWrapper>
                        ) : null}
                        <TopKeywordsTable>
                            {isAfterTrialUser ? (
                                renderAfterTrialComponent()
                            ) : (
                                <MiniFlexTable
                                    tableData={response}
                                    tableColumns={isCompare ? TableCompareColumns : TableColumns}
                                    tableOptions={isCompare ? CompareOptions : Options}
                                />
                            )}
                        </TopKeywordsTable>
                        {hideFooter ? null : (
                            <WidgetFooter
                                {...href}
                                trackingLabel={trackingLabel}
                                text={buttonText}
                                unlockHook={unlockHook}
                            />
                        )}
                    </div>
                )}
            </TopKeywordsContainer>
        </div>
    );
};

const propsAreEqual = (prevProps, nextProps) => prevProps.queryParams?.keys === nextProps.queryParams?.keys;
const TopKeywords = React.memo(TopKeywordsInner, propsAreEqual);
